import { useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import {
  PopupContent,
  type PopupContentProps,
} from '../utils/overview-map-tooltip-content';

interface TooltipState {
  isOpen: boolean;
  content: PopupContentProps | null;
  x: number;
  y: number;
  anchor:
    | 'left'
    | 'right'
    | 'top'
    | 'bottom'
    | 'top-left'
    | 'top-right'
    | 'bottom-left'
    | 'bottom-right';
}

export function useTooltipPortal(
  tooltipState: TooltipState | null,
  onMeasure?: (size: { width: number; height: number }) => void
) {
  const contentRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!tooltipState?.isOpen) return;
    const element = contentRef.current;
    if (!element) return;

    const measure = () => {
      const rect = element.getBoundingClientRect();
      onMeasure?.({
        width: Math.max(1, Math.round(rect.width)),
        height: Math.max(1, Math.round(rect.height)),
      });
    };

    measure();

    const observer = new ResizeObserver(() => {
      measure();
    });
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [tooltipState?.isOpen, onMeasure]);

  const portal = useMemo(() => {
    if (!tooltipState?.isOpen) return null;
    if (typeof document === 'undefined') return null;

    const tooltipStyle: React.CSSProperties = {
      position: 'fixed',
      pointerEvents: 'none',
      zIndex: 9999,
      left: `${tooltipState.x}px`,
      top: `${tooltipState.y}px`,
      ...(tooltipState.anchor === 'left' && {
        transform: 'translate(0, -50%)',
      }),
      ...(tooltipState.anchor === 'right' && {
        transform: 'translate(-100%, -50%)',
      }),
      ...(tooltipState.anchor === 'top' && {
        transform: 'translate(-50%, 0)',
      }),
      ...(tooltipState.anchor === 'top-left' && {
        transform: 'translate(0, 0)',
      }),
      ...(tooltipState.anchor === 'top-right' && {
        transform: 'translate(-100%, 0)',
      }),
      ...(tooltipState.anchor === 'bottom' && {
        transform: 'translate(-50%, -100%)',
      }),
      ...(tooltipState.anchor === 'bottom-left' && {
        transform: 'translate(0, -100%)',
      }),
      ...(tooltipState.anchor === 'bottom-right' && {
        transform: 'translate(-100%, -100%)',
      }),
    };

    const anchorClassName = `maplibregl-popup-anchor-${tooltipState.anchor}`;

    return createPortal(
      <div
        className={`maplibregl-popup ${anchorClassName}`}
        style={tooltipStyle}
      >
        <div className="maplibregl-popup-tip" />
        <div className="maplibregl-popup-content" ref={contentRef}>
          {tooltipState.content ? (
            <PopupContent {...tooltipState.content} />
          ) : null}
        </div>
      </div>,
      document.body
    );
  }, [tooltipState]);

  return portal;
}
